import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  DrillApprover,
  SafetyActivationCondition,
  SafetyOperationsInput,
  SafetyOperationsView,
  acceptSafetyAssignment,
  approveSafetyDrill,
  getSafetyOperations,
  updateSafetyOperations,
} from '../api/safety-reports';
import { UTC_ZONE, formatShortDateTime } from '../time/zonedTime';
import { Note, OpsPage, Pill, Section } from '../../../design-system/ops';

/**
 * Safety operation — `/admin/safety/operations` (MC-09 v1-1).
 *
 * Product's decision: the report button is switched on by evidence, not by a
 * deploy. This screen is where that evidence is recorded and read back:
 *
 *  - the nine activation conditions of MC-09 §4, each met or listing what is
 *    still missing, as the server computes them;
 *  - the named titular and suplente accept as themselves, and the drill is
 *    signed by titular, suplente and Operations against the version in force;
 *  - the configuration form, which writes a new version every time, with a
 *    reason.
 *
 * Nobody is pre-filled. The form starts empty because MC-09 designates no
 * person, no timing and no country — Product and Ops do.
 *
 * Reading is open to operators; saving needs ADMIN with the standing Trust &
 * Safety role and MFA, which the server enforces.
 */

const CONDITION_LABELS: Record<SafetyActivationCondition, string> = {
  OWNERS: 'Titular y suplente',
  COVERAGE: 'Cobertura',
  PREVENTIVE_MEASURE: 'Medida preventiva autorizada',
  PRIORITIES_AND_TIMES: 'Prioridades y tiempos',
  COUNTRY_PROCEDURE: 'Procedimiento por país',
  PERMISSIONS: 'Permisos',
  CONTINUITY: 'Continuidad',
  AGE_RANGE: 'Rango de edad',
  OPERATIONAL_DRILL: 'Prueba operativa (simulacro)',
};

const APPROVER_LABELS: Record<DrillApprover, string> = {
  PRIMARY: 'Titular',
  BACKUP: 'Suplente',
  OPERATIONS: 'Operaciones',
};

const DAYS: Array<{ value: string; label: string }> = [
  { value: 'MON', label: 'Lun' },
  { value: 'TUE', label: 'Mar' },
  { value: 'WED', label: 'Mié' },
  { value: 'THU', label: 'Jue' },
  { value: 'FRI', label: 'Vie' },
  { value: 'SAT', label: 'Sáb' },
  { value: 'SUN', label: 'Dom' },
];

const fmt = (iso: string) => `${formatShortDateTime(iso, UTC_ZONE)} UTC`;

/** Everything the form edits, as strings, so an empty field stays empty. */
interface Draft {
  primaryId: string;
  primaryRole: string;
  primaryEmail: string;
  primaryOther: string;
  backupId: string;
  backupRole: string;
  backupEmail: string;
  backupOther: string;
  timezone: string;
  windows: Array<{ days: string[]; start: string; end: string }>;
  outOfHours: string;
  absenceSubstitution: string;
  measureDescription: string;
  measureAuthority: string;
  measureScope: string;
  measureCutsAccess: boolean;
  measureDocument: string;
  measureReview: string;
  procedureDocument: string;
  procedureApprovedBy: string;
  procedureApprovedAt: string;
  ackMinutes: string;
  countries: Array<{
    countryCode: string;
    urgentProcedureRef: string;
    learnerGuidance: string;
    verifiedBy: string;
    verifiedAt: string;
    legalReviewRef: string;
  }>;
  permissionsDocument: string;
  permissionsReviewedBy: string;
  permissionsReviewedAt: string;
  alternativeChannel: string;
  deliveryFailureRef: string;
  ageMin: string;
  ageMax: string;
  drillCompletedAt: string;
  drillEvidence: string;
  drillEscalated: boolean;
  drillMeasureExecuted: boolean;
}

const s = (v: unknown) => (v === undefined || v === null ? '' : String(v));

function toDraft(c: SafetyOperationsInput | undefined): Draft {
  return {
    primaryId: s(c?.primary?.operatorId),
    primaryRole: s(c?.primary?.role),
    primaryEmail: s(c?.primary?.alertEmail),
    primaryOther: s(c?.primary?.otherContact),
    backupId: s(c?.backup?.operatorId),
    backupRole: s(c?.backup?.role),
    backupEmail: s(c?.backup?.alertEmail),
    backupOther: s(c?.backup?.otherContact),
    timezone: s(c?.coverage?.timezone),
    windows: c?.coverage?.windows?.map((w) => ({ ...w })) ?? [],
    outOfHours: s(c?.coverage?.outOfHours),
    absenceSubstitution: s(c?.coverage?.absenceSubstitution),
    measureDescription: s(c?.preventiveMeasure?.description),
    measureAuthority: s(c?.preventiveMeasure?.authority),
    measureScope: s(c?.preventiveMeasure?.scope),
    measureCutsAccess: c?.preventiveMeasure?.coversLearnerAccessCut ?? false,
    measureDocument: s(c?.preventiveMeasure?.documentRef),
    measureReview: s(c?.preventiveMeasure?.reviewDate),
    procedureDocument: s(c?.procedure?.documentRef),
    procedureApprovedBy: s(c?.procedure?.approvedBy),
    procedureApprovedAt: s(c?.procedure?.approvedAt),
    ackMinutes: s(c?.procedure?.acknowledgeWithinMinutes),
    countries: c?.countries?.map((k) => ({ ...k })) ?? [],
    permissionsDocument: s(c?.permissions?.documentRef),
    permissionsReviewedBy: s(c?.permissions?.reviewedBy),
    permissionsReviewedAt: s(c?.permissions?.reviewedAt),
    alternativeChannel: s(c?.continuity?.alternativeChannel),
    deliveryFailureRef: s(c?.continuity?.deliveryFailureProcedureRef),
    ageMin: s(c?.ageRange?.min),
    ageMax: s(c?.ageRange?.max),
    drillCompletedAt: s(c?.drill?.completedAt),
    drillEvidence: s(c?.drill?.evidenceRef),
    drillEscalated: c?.drill?.escalatedToBackup ?? false,
    drillMeasureExecuted: c?.drill?.preventiveMeasureExecuted ?? false,
  };
}

const filled = (...values: string[]) => values.some((v) => v.trim() !== '');
const t = (v: string) => v.trim();

/** Only the sections someone started filling are sent: an empty section is "not declared". */
function fromDraft(d: Draft): SafetyOperationsInput {
  const out: SafetyOperationsInput = {};
  if (filled(d.primaryId, d.primaryRole, d.primaryEmail)) {
    out.primary = {
      operatorId: t(d.primaryId),
      role: t(d.primaryRole),
      alertEmail: t(d.primaryEmail),
      ...(t(d.primaryOther) ? { otherContact: t(d.primaryOther) } : {}),
    };
  }
  if (filled(d.backupId, d.backupRole, d.backupEmail)) {
    out.backup = {
      operatorId: t(d.backupId),
      role: t(d.backupRole),
      alertEmail: t(d.backupEmail),
      ...(t(d.backupOther) ? { otherContact: t(d.backupOther) } : {}),
    };
  }
  if (filled(d.timezone, d.outOfHours, d.absenceSubstitution) || d.windows.length > 0) {
    out.coverage = {
      timezone: t(d.timezone),
      windows: d.windows,
      outOfHours: t(d.outOfHours),
      absenceSubstitution: t(d.absenceSubstitution),
    };
  }
  if (filled(d.measureDescription, d.measureAuthority, d.measureScope, d.measureDocument)) {
    out.preventiveMeasure = {
      mechanism: 'PROVISIONAL_MANUAL',
      description: t(d.measureDescription),
      authority: t(d.measureAuthority),
      scope: t(d.measureScope),
      coversLearnerAccessCut: d.measureCutsAccess,
      documentRef: t(d.measureDocument),
      reviewDate: t(d.measureReview),
    };
  }
  if (filled(d.procedureDocument, d.procedureApprovedBy, d.ackMinutes)) {
    out.procedure = {
      documentRef: t(d.procedureDocument),
      approvedBy: t(d.procedureApprovedBy),
      approvedAt: t(d.procedureApprovedAt),
      acknowledgeWithinMinutes: Number(d.ackMinutes),
    };
  }
  if (d.countries.length > 0) {
    out.countries = d.countries.map((k) => ({ ...k, countryCode: t(k.countryCode).toUpperCase() }));
  }
  if (filled(d.permissionsDocument, d.permissionsReviewedBy)) {
    out.permissions = {
      documentRef: t(d.permissionsDocument),
      reviewedBy: t(d.permissionsReviewedBy),
      reviewedAt: t(d.permissionsReviewedAt),
    };
  }
  if (filled(d.alternativeChannel, d.deliveryFailureRef)) {
    out.continuity = {
      alternativeChannel: t(d.alternativeChannel),
      deliveryFailureProcedureRef: t(d.deliveryFailureRef),
    };
  }
  if (filled(d.ageMin, d.ageMax)) {
    out.ageRange = { min: Number(d.ageMin), max: Number(d.ageMax) };
  }
  if (filled(d.drillCompletedAt, d.drillEvidence)) {
    out.drill = {
      completedAt: t(d.drillCompletedAt),
      evidenceRef: t(d.drillEvidence),
      escalatedToBackup: d.drillEscalated,
      preventiveMeasureExecuted: d.drillMeasureExecuted,
    };
  }
  return out;
}

const fieldStyle: React.CSSProperties = { display: 'block', margin: '8px 0' };

const Text: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  type?: string;
}> = ({ id, label, value, onChange, multiline, type = 'text' }) => (
  <div style={fieldStyle}>
    <label htmlFor={id} style={{ display: 'block' }}>
      {label}
    </label>
    {multiline ? (
      <textarea
        id={id}
        className="inp"
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%' }}
      />
    ) : (
      <input
        id={id}
        className="inp"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%' }}
      />
    )}
  </div>
);

const Check: React.FC<{
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ id, label, checked, onChange }) => (
  <div style={fieldStyle}>
    <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />{' '}
    <label htmlFor={id}>{label}</label>
  </div>
);

const AdminSafetyOperations: React.FC = () => {
  const [view, setView] = useState<SafetyOperationsView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(toDraft(undefined));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'plain' | 'bad'; text: string } | null>(null);

  const apply = useCallback((v: SafetyOperationsView) => {
    setView(v);
    setDraft(toDraft(v.config));
  }, []);

  useEffect(() => {
    let cancelled = false;
    getSafetyOperations()
      .then((v) => !cancelled && apply(v))
      .catch((e: any) => !cancelled && setLoadError(e?.message || 'No se pudo cargar'));
    return () => {
      cancelled = true;
    };
  }, [apply]);

  const run = async (call: () => Promise<SafetyOperationsView>, done: string) => {
    setBusy(true);
    setMessage(null);
    try {
      apply(await call());
      setMessage({ tone: 'plain', text: done });
    } catch (e: any) {
      setMessage({ tone: 'bad', text: e?.message || 'No se pudo guardar' });
    } finally {
      setBusy(false);
    }
  };

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  if (loadError) {
    return (
      <OpsPage title="Operación de seguridad">
        <Note tone="bad">{loadError}</Note>
      </OpsPage>
    );
  }
  if (!view) {
    return (
      <OpsPage title="Operación de seguridad">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const { assessment } = view;
  const status = assessment.ready
    ? { tone: 'ok' as const, text: 'Lista para activar' }
    : assessment.drillReady
    ? { tone: 'warn' as const, text: 'Lista para el simulacro' }
    : { tone: 'bad' as const, text: 'No lista: el botón sigue apagado' };

  const save = () =>
    run(
      () =>
        updateSafetyOperations({
          ...fromDraft(draft),
          reason: reason.trim(),
          expectedVersion: view.config?.version ?? 0,
        }),
      'Guardamos una nueva versión.',
    );

  return (
    <OpsPage
      title="Operación de seguridad"
      subtitle="Condiciones de activación del reporte de seguridad (MC-09)"
    >
      <p>
        <Link to="/admin/safety">Volver a la bandeja de Safety</Link>
      </p>
      <Note>
        El reporte de seguridad solo se activa cuando estas nueve condiciones tienen evidencia y el
        interruptor está encendido. Un botón apagado es un estado honesto; uno encendido sin
        respuesta detrás no lo es.
      </Note>
      {message ? <Note tone={message.tone}>{message.text}</Note> : null}

      <Section
        title="Condiciones de activación"
        subtitle={view.config ? `Versión ${view.config.version}` : 'Sin configurar'}
        action={<Pill tone={status.tone}>{status.text}</Pill>}
      >
        <ul aria-label="Condiciones de activación" style={{ listStyle: 'none', padding: 0 }}>
          {assessment.conditions.map((c) => (
            <li key={c.condition} style={{ margin: '8px 0' }}>
              <Pill tone={c.met ? 'ok' : 'bad'}>{c.met ? 'Cumple' : 'Falta'}</Pill>{' '}
              <strong>{CONDITION_LABELS[c.condition] ?? c.condition}</strong>
              {c.missing.length > 0 ? (
                <ul>
                  {c.missing.map((m) => (
                    <li key={m} className="mut">
                      {m}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Aceptación y simulacro">
        <p className="mut">
          El titular y el suplente aceptan la asignación con su propia cuenta. El simulacro lo
          firman el titular, el suplente y Operaciones sobre la versión vigente.
        </p>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {view.acceptances.map((a) => (
            <li key={a.position}>
              {a.position === 'PRIMARY' ? 'Titular' : 'Suplente'} {a.operatorId} aceptó ·{' '}
              {fmt(a.acceptedAt)}
            </li>
          ))}
          {view.drillApprovals.map((a) => (
            <li key={a.approver}>
              Simulacro firmado por {APPROVER_LABELS[a.approver]} ({a.operatorId}) sobre la versión{' '}
              {a.configVersion}
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="btn btn-p"
          disabled={busy}
          onClick={() => run(acceptSafetyAssignment, 'Registramos tu aceptación.')}
        >
          Aceptar mi asignación
        </button>{' '}
        {(['PRIMARY', 'BACKUP', 'OPERATIONS'] as DrillApprover[]).map((a) => (
          <React.Fragment key={a}>
            <button
              type="button"
              className="btn btn-o"
              disabled={busy}
              onClick={() =>
                run(() => approveSafetyDrill(a), 'Registramos tu firma del simulacro.')
              }
            >
              Firmar simulacro como {APPROVER_LABELS[a]}
            </button>{' '}
          </React.Fragment>
        ))}
      </Section>

      <Section title="Configuración" subtitle="Cada cambio crea una versión nueva">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <fieldset>
            <legend>Titular</legend>
            <Text
              id="p-id"
              label="Id de operador del titular"
              value={draft.primaryId}
              onChange={(v) => set('primaryId', v)}
            />
            <Text
              id="p-role"
              label="Función del titular"
              value={draft.primaryRole}
              onChange={(v) => set('primaryRole', v)}
            />
            <Text
              id="p-email"
              label="Correo operativo del titular (no una bandeja general)"
              type="email"
              value={draft.primaryEmail}
              onChange={(v) => set('primaryEmail', v)}
            />
            <Text
              id="p-other"
              label="Otro contacto operativo del titular"
              value={draft.primaryOther}
              onChange={(v) => set('primaryOther', v)}
            />
          </fieldset>
          <fieldset>
            <legend>Suplente</legend>
            <Text
              id="b-id"
              label="Id de operador del suplente"
              value={draft.backupId}
              onChange={(v) => set('backupId', v)}
            />
            <Text
              id="b-role"
              label="Función del suplente"
              value={draft.backupRole}
              onChange={(v) => set('backupRole', v)}
            />
            <Text
              id="b-email"
              label="Correo operativo del suplente (no una bandeja general)"
              type="email"
              value={draft.backupEmail}
              onChange={(v) => set('backupEmail', v)}
            />
            <Text
              id="b-other"
              label="Otro contacto operativo del suplente"
              value={draft.backupOther}
              onChange={(v) => set('backupOther', v)}
            />
          </fieldset>
          <fieldset>
            <legend>Cobertura</legend>
            <Text
              id="c-tz"
              label="Zona horaria (IANA, por ejemplo America/Mexico_City)"
              value={draft.timezone}
              onChange={(v) => set('timezone', v)}
            />
            {draft.windows.map((w, i) => (
              // Windows have no identity of their own; the position is it.
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} style={{ border: '1px solid #ddd', padding: 8, margin: '8px 0' }}>
                <fieldset style={{ border: 0, padding: 0 }}>
                  <legend>Horario {i + 1}</legend>
                  {DAYS.map((d) => (
                    <React.Fragment key={d.value}>
                      <input
                        id={`w${i}-${d.value}`}
                        type="checkbox"
                        checked={w.days.includes(d.value)}
                        onChange={(e) =>
                          set(
                            'windows',
                            draft.windows.map((x, j) =>
                              j === i
                                ? {
                                    ...x,
                                    days: e.target.checked
                                      ? [...x.days, d.value]
                                      : x.days.filter((y) => y !== d.value),
                                  }
                                : x,
                            ),
                          )
                        }
                      />{' '}
                      <label htmlFor={`w${i}-${d.value}`}>{d.label}</label>{' '}
                    </React.Fragment>
                  ))}
                </fieldset>
                <Text
                  id={`w${i}-start`}
                  label="Desde (HH:MM)"
                  value={w.start}
                  onChange={(v) =>
                    set(
                      'windows',
                      draft.windows.map((x, j) => (j === i ? { ...x, start: v } : x)),
                    )
                  }
                />
                <Text
                  id={`w${i}-end`}
                  label="Hasta (HH:MM)"
                  value={w.end}
                  onChange={(v) =>
                    set(
                      'windows',
                      draft.windows.map((x, j) => (j === i ? { ...x, end: v } : x)),
                    )
                  }
                />
                <button
                  type="button"
                  className="btn btn-o"
                  onClick={() =>
                    set(
                      'windows',
                      draft.windows.filter((_, j) => j !== i),
                    )
                  }
                >
                  Quitar horario {i + 1}
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-o"
              onClick={() => set('windows', [...draft.windows, { days: [], start: '', end: '' }])}
            >
              Añadir horario
            </button>
            <Text
              id="c-ooh"
              label="Atención fuera de horario"
              multiline
              value={draft.outOfHours}
              onChange={(v) => set('outOfHours', v)}
            />
            <Text
              id="c-abs"
              label="Sustitución por ausencia"
              multiline
              value={draft.absenceSubstitution}
              onChange={(v) => set('absenceSubstitution', v)}
            />
          </fieldset>
          <fieldset>
            <legend>Medida preventiva (vía provisional hasta UC-A11)</legend>
            <Text
              id="m-desc"
              label="Qué se hace, quién y con qué herramienta"
              multiline
              value={draft.measureDescription}
              onChange={(v) => set('measureDescription', v)}
            />
            <Text
              id="m-auth"
              label="Quién la autoriza"
              value={draft.measureAuthority}
              onChange={(v) => set('measureAuthority', v)}
            />
            <Text
              id="m-scope"
              label="Alcance"
              multiline
              value={draft.measureScope}
              onChange={(v) => set('measureScope', v)}
            />
            <Check
              id="m-cut"
              label="Cubre cortar el acceso de un learner"
              checked={draft.measureCutsAccess}
              onChange={(v) => set('measureCutsAccess', v)}
            />
            <Text
              id="m-doc"
              label="Declaración escrita (referencia)"
              value={draft.measureDocument}
              onChange={(v) => set('measureDocument', v)}
            />
            <Text
              id="m-review"
              label="Fecha de revisión"
              type="date"
              value={draft.measureReview}
              onChange={(v) => set('measureReview', v)}
            />
          </fieldset>
          <fieldset>
            <legend>Prioridades y tiempos (procedimiento operativo)</legend>
            <Text
              id="pr-doc"
              label="Procedimiento operativo (referencia)"
              value={draft.procedureDocument}
              onChange={(v) => set('procedureDocument', v)}
            />
            <Text
              id="pr-by"
              label="Aprobado por"
              value={draft.procedureApprovedBy}
              onChange={(v) => set('procedureApprovedBy', v)}
            />
            <Text
              id="pr-at"
              label="Aprobado el"
              type="date"
              value={draft.procedureApprovedAt}
              onChange={(v) => set('procedureApprovedAt', v)}
            />
            <Text
              id="pr-ack"
              label="Minutos para aceptar la asignación (según el procedimiento)"
              type="number"
              value={draft.ackMinutes}
              onChange={(v) => set('ackMinutes', v)}
            />
          </fieldset>
          <fieldset>
            <legend>Procedimiento por país</legend>
            {draft.countries.map((k, i) => {
              const upd = (field: keyof Draft['countries'][number], v: string) =>
                set(
                  'countries',
                  draft.countries.map((x, j) => (j === i ? { ...x, [field]: v } : x)),
                );
              return (
                // Rows have no identity until a code is typed; the position is it.
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} style={{ border: '1px solid #ddd', padding: 8, margin: '8px 0' }}>
                  <Text
                    id={`k${i}-code`}
                    label={`País ${i + 1} (código de dos letras)`}
                    value={k.countryCode}
                    onChange={(v) => upd('countryCode', v)}
                  />
                  <Text
                    id={`k${i}-proc`}
                    label="Ruta local urgente verificada (referencia)"
                    value={k.urgentProcedureRef}
                    onChange={(v) => upd('urgentProcedureRef', v)}
                  />
                  <Text
                    id={`k${i}-guide`}
                    label="Instrucciones verificadas para el alumno ante peligro inmediato"
                    multiline
                    value={k.learnerGuidance}
                    onChange={(v) => upd('learnerGuidance', v)}
                  />
                  <Text
                    id={`k${i}-vby`}
                    label="Verificado por"
                    value={k.verifiedBy}
                    onChange={(v) => upd('verifiedBy', v)}
                  />
                  <Text
                    id={`k${i}-vat`}
                    label="Verificado el"
                    type="date"
                    value={k.verifiedAt}
                    onChange={(v) => upd('verifiedAt', v)}
                  />
                  <Text
                    id={`k${i}-legal`}
                    label="Revisión de obligaciones (referencia)"
                    value={k.legalReviewRef}
                    onChange={(v) => upd('legalReviewRef', v)}
                  />
                  <button
                    type="button"
                    className="btn btn-o"
                    onClick={() =>
                      set(
                        'countries',
                        draft.countries.filter((_, j) => j !== i),
                      )
                    }
                  >
                    Quitar país {i + 1}
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="btn btn-o"
              onClick={() =>
                set('countries', [
                  ...draft.countries,
                  {
                    countryCode: '',
                    urgentProcedureRef: '',
                    learnerGuidance: '',
                    verifiedBy: '',
                    verifiedAt: '',
                    legalReviewRef: '',
                  },
                ])
              }
            >
              Añadir país
            </button>
          </fieldset>
          <fieldset>
            <legend>Permisos</legend>
            <Text
              id="pe-doc"
              label="Quién consulta, asigna, interviene y cierra (referencia)"
              value={draft.permissionsDocument}
              onChange={(v) => set('permissionsDocument', v)}
            />
            <Text
              id="pe-by"
              label="Revisado por"
              value={draft.permissionsReviewedBy}
              onChange={(v) => set('permissionsReviewedBy', v)}
            />
            <Text
              id="pe-at"
              label="Revisado el"
              type="date"
              value={draft.permissionsReviewedAt}
              onChange={(v) => set('permissionsReviewedAt', v)}
            />
          </fieldset>
          <fieldset>
            <legend>Continuidad</legend>
            <Text
              id="co-alt"
              label="Canal alternativo atendido (texto que verá el alumno si el envío falla)"
              multiline
              value={draft.alternativeChannel}
              onChange={(v) => set('alternativeChannel', v)}
            />
            <Text
              id="co-fail"
              label="Actuación ante fallos de entrega o ausencia del titular (referencia)"
              value={draft.deliveryFailureRef}
              onChange={(v) => set('deliveryFailureRef', v)}
            />
          </fieldset>
          <fieldset>
            <legend>Rango de edad de la superficie (dentro de 3–18)</legend>
            <Text
              id="a-min"
              label="Edad mínima"
              type="number"
              value={draft.ageMin}
              onChange={(v) => set('ageMin', v)}
            />
            <Text
              id="a-max"
              label="Edad máxima"
              type="number"
              value={draft.ageMax}
              onChange={(v) => set('ageMax', v)}
            />
          </fieldset>
          <fieldset>
            <legend>Simulacro</legend>
            <Text
              id="d-at"
              label="Fecha del simulacro"
              type="date"
              value={draft.drillCompletedAt}
              onChange={(v) => set('drillCompletedAt', v)}
            />
            <Text
              id="d-ev"
              label="Evidencia (referencia)"
              value={draft.drillEvidence}
              onChange={(v) => set('drillEvidence', v)}
            />
            <Check
              id="d-esc"
              label="Se escaló al suplente"
              checked={draft.drillEscalated}
              onChange={(v) => set('drillEscalated', v)}
            />
            <Check
              id="d-mea"
              label="Se ejecutó una medida preventiva de extremo a extremo"
              checked={draft.drillMeasureExecuted}
              onChange={(v) => set('drillMeasureExecuted', v)}
            />
          </fieldset>

          <Text id="reason" label="Motivo del cambio" value={reason} onChange={setReason} />
          <button type="submit" className="btn btn-p" disabled={busy || reason.trim() === ''}>
            Guardar nueva versión
          </button>
        </form>
      </Section>

      <Section title="Historial" count={view.history.length}>
        {view.history.length === 0 ? (
          <p className="mut">Todavía no hay ninguna versión.</p>
        ) : (
          <ul>
            {view.history.map((h) => (
              <li key={h.version}>
                Versión {h.version} · {h.updatedBy} · {fmt(h.updatedAt)} · {h.reason}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </OpsPage>
  );
};

export default AdminSafetyOperations;
